<template>
  <div
    :class="[
      appendix && '-mr-5',
      multiline ? 'whitespace-normal font-semibold' : 'whitespace-nowrap font-semibold',
      align === 'center' && 'inline-flex justify-center',
      align === 'right' && 'flex justify-end',
      align === 'left' && 'flex justify-start',
    ]"
  >
    <span
      v-if="value || value === 0"
      :class="[
        color === 'green' || color === 'red' ? colorMap[color + '-' + colorDarkness] : colorMap[color],
        threshold && colorMap[thresholdColor],
        valueMaxWidth,
        'z-[1]',
        multiline
          ? 'inline-flex items-center text-center rounded-lg py-1'
          : 'inline-flex items-center justify-center rounded-full',
        {
          [$style.loading]: loading,
          'h-5 min-w-6 px-2.5 text-xs': size === 'xs' && !multiline,
          'h-6 min-w-8 px-2 text-xs': size === 'sm' && !multiline,
          'h-12 min-w-16 px-4 text-2xl': size === 'md' && !multiline,
          'min-h-5 min-w-6 px-2.5 text-xs': size === 'xs' && multiline,
          'min-h-6 min-w-8 px-2 text-xs': size === 'sm' && multiline,
          'min-h-12 min-w-16 px-4 text-2xl': size === 'md' && multiline,
          'outline outline-white': appendix,
          'rounded-r-none pl-2 pr-1.5 outline-none outline-0': appendix && size === 'xs',
          'rounded-r-none pl-3 pr-2 outline-2': appendix && size === 'sm',
          'outline-4': appendix && size === 'md',
          'mix-blend-multiply': !appendix && !removeBlendMode && (!threshold || !thresholdColor),
          [borderMap[color]]: hasDividingBorder && appendix,
        },
      ]"
    >
      <span :class="multiline ? 'flex items-center gap-1 whitespace-normal text-wrap' : 'flex items-center gap-1 truncate'">
        <slot name="icon" />
        {{ !loading ? value : '' }}
      </span>
    </span>
    <span
      v-if="!loading && appendix"
      :class="[
        colorMap[`${appendixColor}-${colorDarkness}`],
        'relative -left-5 z-0 inline-flex items-center justify-end truncate whitespace-nowrap rounded-e-full text-xs',
        { 'h-5 pl-6 pr-2.5': size === 'xs', 'h-6 pl-7 pr-3': size === 'sm', 'h-12 pl-8 pr-3': size === 'md' },
      ]"
    >
      <span v-if="negativeAppendix">&minus;</span>
      <span v-if="!negativeAppendix && !textual">&plus;</span>
      {{ negativeAppendix ? removeMinus : positiveAppendix ? removePlus : appendix }}
    </span>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { getColorByThreshold } from '../utils/colors'
import { UiPillColor, UiPillDarkness, UiPillSize } from '../types'

const props = withDefaults(
  defineProps<{
    value: string | number
    color?: string
    size?: string
    appendix?: string | number
    loading?: boolean
    appendixColor?: string
    colorDarkness?: UiPillDarkness
    textual?: boolean
    threshold?: number | null
    align?: 'left' | 'center' | 'right'
    hasDividingBorder?: boolean
    removeBlendMode?: boolean
    valueMaxWidth?: string
    multiline?: boolean
  }>(),
  {
    value: 'N/A',
    color: UiPillColor.Default,
    size: UiPillSize.Medium,
    appendix: '',
    loading: false,
    appendixColor: UiPillColor.Green,
    colorDarkness: UiPillDarkness.Default,
    textual: false,
    threshold: 0,
    align: 'center',
    hasDividingBorder: false,
    removeBlendMode: false,
    valueMaxWidth: 'max-w-full',
    multiline: false,
  },
)

const negativeAppendix = computed(() => props.appendix?.toString().startsWith('-'))
const removeMinus = computed(() => negativeAppendix.value && props.appendix.toString().slice(1))
const positiveAppendix = computed(() => props.appendix?.toString().startsWith('+'))
const removePlus = computed(() => positiveAppendix.value && props.appendix.toString().slice(1))
const thresholdColor = computed(() => {
  return getColorByThreshold(parseInt(props.value.toString(), 10), props.threshold || 0)
})

const colorMap = {
  default: 'text-gray-60 bg-gray-10',
  brand: 'text-pill-brand-color bg-pill-brand-bg',
  blue: 'text-blue-60 bg-blue-10',
  yellow: 'text-yellow-60 bg-yellow-10',
  'gray-30': 'text-gray-80 bg-gray-30',
  'orange-20': 'text-orange-70 bg-orange-20',
  'red-10': 'text-red-60 bg-red-10',
  'red-20': 'text-red-70 bg-red-20',
  'red-30': 'text-red-80 bg-red-30',
  'green-10': 'text-green-60 bg-green-10',
  'green-20': 'text-green-70 bg-green-20',
  'green-30': 'text-green-80 bg-green-30',
  'blue-10': 'text-blue-60 bg-blue-10',
  dispute_NEW: 'text-gray-80 bg-gray-20',
  dispute_ACCEPTED: 'text-green-80 bg-green-20',
  dispute_REJECTED: 'text-red-80 bg-red-20',
  dispute_PARTIALLY_ACCEPTED: 'text-teal-80 bg-teal-20',
  zblue: 'text-zblue-60 bg-zblue-10',
}

const borderMap = {
  blue: 'border-r-2 border-blue-20',
}
</script>

<style lang="scss" module>
@keyframes statsBubbleLoading {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.loading {
  background: linear-gradient(90deg, var(--gray-30), #f2f4f8 33%, var(--gray-20));
  background-size: 400%;
  width: 76px;
  height: 44px;
  animation: statsBubbleLoading 3s linear infinite;
}
</style>
